<script setup lang="ts">
import { computed, ref, watch } from "vue";
import type { ComponentExposed } from "vue-component-type-helpers";

import ColorPicker from "~/components/dumb/ColorPicker.vue";
import DropdownMenu from "~/components/dumb/DropdownMenu.vue";
import StatusIcon from "~/components/dumb/StatusIcon.vue";
import Tooltip from "~/components/dumb/Tooltip.vue";
import { copyAndNotify } from "~/components/notifications";
import { ColorPickerIcon, DuidFieldIcon, RenameIcon, TrashIcon } from "~/icons";
import { DropdownMenuItemKind } from "~/shared/enums";
import type { Status } from "~/shared/types";
import { useDataStore } from "~/stores";

import SettingsTableItem from "./SettingsTableItem.vue";

const DELETE_STATUS_DIALOG_DESCRIPTION =
  "Permanently deleting a status will move all its tasks to the default status. This can't be undone. Are you sure you want to proceed?";

const props = defineProps<{
  status: Status;
  isFirst?: boolean;
}>();

const emit = defineEmits<{
  edit: [status: { duid: string; title?: string; colorHex?: string }];
  delete: [];
  changePrevented: [];
}>();

const dataStore = useDataStore();

const colorPickerMenu = ref<InstanceType<typeof DropdownMenu> | null>(null);
const tableItemRef = ref<ComponentExposed<typeof SettingsTableItem> | null>(null);

const color = ref(props.status.colorHex);

watch(
  () => props.status,
  (newStatus: Status) => {
    color.value = newStatus.colorHex;
  }
);

const titleExists = (title: string) => {
  const property = dataStore.getPropertyByDuid(props.status.propertyDuid);
  if (!property) {
    return true;
  }

  return dataStore
    .getStatusList(property)
    .some((status) => status.title.toLowerCase() === title.trim().toLowerCase() && status.duid !== props.status.duid);
};

const onSave = async (newTitle: string) => {
  emit("edit", {
    duid: props.status.duid,
    title: newTitle,
    colorHex: color.value,
  });
};

const onDelete = () => {
  emit("delete");
};

const startEditing = () => {
  tableItemRef.value?.startEditing();
};

const openColorPicker = () => {
  colorPickerMenu.value?.open();
};

const colorPickerSections = computed(() => [
  {
    title: "Modify",
    items: [
      {
        title: "Edit status",
        kind: DropdownMenuItemKind.COMPONENT,
        noFocus: true,
        component: ColorPicker,
        componentArgs: {
          value: color.value,
          onSelect: (colorHex: string) => {
            color.value = colorHex;
            emit("edit", { duid: props.status.duid, colorHex });
          },
        },
      },
    ],
  },
]);

const dropdownSections = computed(() => [
  {
    title: "Status",
    items: [
      {
        title: "Rename",
        kind: DropdownMenuItemKind.BUTTON,
        icon: RenameIcon,
        onClick: startEditing,
      },
      {
        title: "Change color",
        kind: DropdownMenuItemKind.BUTTON,
        icon: ColorPickerIcon,
        onClick: openColorPicker,
      },
      {
        title: "Copy ID",
        kind: DropdownMenuItemKind.BUTTON,
        icon: DuidFieldIcon,
        onClick: () => copyAndNotify("Status ID", props.status.duid),
      },
      {
        title: "Delete",
        kind: DropdownMenuItemKind.BUTTON,
        icon: TrashIcon,
        disabled: props.status.locked,
        disabledReason: props.status.locked ? "This is a default status" : undefined,
        onClick: () => tableItemRef.value?.openDeleteDialog(),
      },
    ],
  },
]);

defineExpose({
  duid: computed(() => props.status.duid),
  startEditing,
});
</script>

<template>
  <SettingsTableItem
    ref="tableItemRef"
    :item="status"
    :is-first="isFirst"
    :delete-dialog-title="`Delete ${status.title}`"
    :delete-dialog-description="DELETE_STATUS_DIALOG_DESCRIPTION"
    :dropdown-sections="dropdownSections"
    locked-tooltip="This is a default status and can't be deleted or moved"
    :title-exists="titleExists"
    :on-save="onSave"
    :on-delete="onDelete">
    <template #icon>
      <DropdownMenu ref="colorPickerMenu" :sections="colorPickerSections" :distance="0" :max-height-pixels="460">
        <Tooltip text="Change color">
          <div class="mx-1 flex shrink-0 cursor-pointer items-center justify-center rounded-sm p-1 hover:bg-md">
            <StatusIcon :duid="status.duid" :color-hex="color" class="icon-md" />
          </div>
        </Tooltip>
      </DropdownMenu>
    </template>
  </SettingsTableItem>
</template>
